import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Music24Capabilities } from 'fym-common/src/license/music24Plans';
import { TypedMemo } from '../../common/types';
import { subscriptionMemberMessages as messages } from './subscriptionMemberMessages';
import { Panel } from '../../music24/components/primitives/Panel';
import styles from './SubscriptionMemberLimits.module.scss';

interface SubscriptionMemberLimitsProps {
  capabilities: Music24Capabilities;
  /** `workspaces.list` artist count of the active workspace. */
  artistCount: number;
  /** Members plus open invites (`team.list`). Undefined while it loads. */
  seatsUsed: number | undefined;
}

interface LimitTile {
  key: string;
  label: string;
  value: string;
  suffix: string;
}

/**
 * design: workspaces f1 .lims: the limits of the workspace plan, with the use where the client
 * knows it. Social accounts have no client count, so that tile shows the limit only.
 */
const SubscriptionMemberLimitsComponent: React.FC<SubscriptionMemberLimitsProps> = ({
  capabilities,
  artistCount,
  seatsUsed,
}) => {
  const intl = useIntl();
  const tiles = useMemo((): LimitTile[] => {
    const unlimited = intl.formatMessage(messages.unlimited);
    const counted = (used: number | undefined, limit: number | null) =>
      limit === null
        ? { value: unlimited, suffix: '' }
        : used === undefined
          ? { value: String(limit), suffix: intl.formatMessage(messages.max) }
          : { value: String(used), suffix: intl.formatMessage(messages.ofLimit, { limit }) };
    return [
      {
        key: 'artists',
        label: intl.formatMessage(messages.artists),
        ...counted(artistCount, capabilities.artistsLimit),
      },
      { key: 'seats', label: intl.formatMessage(messages.seats), ...counted(seatsUsed, capabilities.seatsLimit) },
      {
        key: 'history',
        label: intl.formatMessage(messages.history),
        value: String(capabilities.historyDaysLimit),
        suffix: intl.formatMessage(messages.days),
      },
      {
        key: 'social',
        label: intl.formatMessage(messages.social),
        ...counted(undefined, capabilities.socialAccountsLimit),
      },
    ];
  }, [intl, artistCount, seatsUsed, capabilities]);

  return (
    <div className={styles.limits}>
      {tiles.map((tile) => (
        <Panel nested key={tile.key} className={styles.limit} data-testid={`member-limit-${tile.key}`}>
          <div className={styles.limitKey}>{tile.label}</div>
          <div className={styles.limitValue}>
            {tile.value} <span>{tile.suffix}</span>
          </div>
        </Panel>
      ))}
    </div>
  );
};

export const SubscriptionMemberLimits = TypedMemo(SubscriptionMemberLimitsComponent);
